import { DatePicker, Form, Row, Space } from 'antd'
import { FormProps, useForm, useWatch } from 'antd/lib/form/Form'
import { PunchListFormValues } from './types'
import { useProjectSettingsContext } from '../../../../../../ProjectSettingsContext'
import { useEffect, useState } from 'react'
import { toMoment } from 'utils/toMoment'
import { useUpsertPunchListIfiSettingsMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { IFIReportType, notEqual } from '@cims-online/shared'
import CIMSFormItem from 'components/CIMSFormItem'
import { HelpMessage } from 'utils/constants'
import CIMSSwitch from 'components/CIMSSwitch'
import { CommonSimpleEditPanelProps } from '../../../common'
import { ifiSettingsEditNotification, momentNotEqual } from '../../../../utils'
import CIMSPrompt from 'components/CIMSPrompt'
import EditPanelFooter from '../../../../components/EditPanelFooter'
import { useProjectContext } from 'context/ProjectContext'

const { type: reportType } = IFIReportType.DailyInspectionPunchList

const PunchListEditPanel: React.VFC<CommonSimpleEditPanelProps> = ({
  isDirty,
  setIsDirty,
}) => {
  const { projectId } = useProjectContext()
  const {
    punchListIFISettings: { data, refetch },
  } = useProjectSettingsContext()
  const [upsert, { loading }] = useUpsertPunchListIfiSettingsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => ifiSettingsEditNotification(reportType),
  })
  const [initialValues, setInitialValues] = useState<PunchListFormValues>({})
  const [form] = useForm<PunchListFormValues>()
  const enabledWatch = useWatch('enabled', form)

  const handleOnValuesChange: FormProps['onValuesChange'] = (
    _,
    currentValues
  ) => {
    setIsDirty(
      notEqual(initialValues.enabled, currentValues.enabled) ||
        momentNotEqual(initialValues.effectiveDate, currentValues.effectiveDate)
    )
  }

  useEffect(() => {
    form.setFieldsValue(initialValues)
    handleOnValuesChange({}, form.getFieldsValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setInitialValues({
      enabled: data?.enabled,
      effectiveDate: toMoment(data?.effectiveDate),
    })
  }, [data])

  return (
    <CIMSPrompt when={isDirty}>
      <Form
        form={form}
        onValuesChange={handleOnValuesChange}
        initialValues={initialValues}
        onFinish={async ({ enabled, effectiveDate }) => {
          if (!isDirty) return
          await upsert({
            variables: {
              input: {
                projectId,
                enabled,
                effectiveDate:
                  effectiveDate === null ? null : effectiveDate?.toISOString(),
              },
            },
          })
          refetch()
        }}
      >
        <Row>
          <Space size="large">
            <CIMSFormItem
              label="Enabled"
              name="enabled"
              help={
                HelpMessage.Administration.ProjectSettings.IFITracker.Enabled
              }
              valuePropName="checked"
            >
              <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
            </CIMSFormItem>
            <CIMSFormItem
              label="Effective Date"
              name="effectiveDate"
              disabled={{
                disabled: !enabledWatch,
                reason: {
                  why: 'disabled',
                  message: `Cannot edit while ${reportType} is disabled`,
                },
              }}
              help={
                HelpMessage.Administration.ProjectSettings.IFITracker
                  .EffectiveDate
              }
            >
              <DatePicker disabled={!enabledWatch} />
            </CIMSFormItem>
          </Space>
        </Row>
        <Row>
          <EditPanelFooter
            {...{
              form,
              loading,
              handleOnValuesChange,
              hasChanges: isDirty,
            }}
          />
        </Row>
      </Form>
    </CIMSPrompt>
  )
}

export default PunchListEditPanel
